// @ts-check
import React from 'react';

import FormControl from '@liquidweb-dev/undercurrent/FormControl';
import FormHelperText from '@liquidweb-dev/undercurrent/FormHelperText';
import FormLabel from '@liquidweb-dev/undercurrent/FormLabel';
import RadioGroup from '@liquidweb-dev/undercurrent/RadioGroup';
import { useInput } from 'components/LWForm/LWForm';

/**
 * @typedef PropsI
 * @property {string} name
 * @property {import('react').ReactNode} [label] children of FormLabel
 * @property {import('react').ReactNode} [helperText] children of FormHelperText
 * @property {boolean} [required] If `true`, the label will indicate that the `input` is required.
 * @property {Omit<import('@liquidweb-dev/undercurrent/FormControl').FormControlProps, 'required'>} [formControlProps]
 * @property {import('@liquidweb-dev/undercurrent/FormHelperText').FormHelperTextProps} [formHelperTextProps]
 * @property {import('@liquidweb-dev/undercurrent/FormLabel').FormLabelProps} [formLabelProps]
 * */

/**
 * A checkbox input component to be used within the context of LWForm
 * @param {Omit<import('@liquidweb-dev/undercurrent/RadioGroup').RadioGroupProps, 'name' | 'required'> & PropsI} props
 */
const HookedRadioGroup = ({
	helperText,
	label,
	name,
	onChange,
	required,
	formControlProps,
	formHelperTextProps,
	formLabelProps,
	...rest
}) => {
	const { value, onValueChange, error, validateSimple } = useInput(name);

	if (!name) {
		return null;
	}

	/** @type {import('@liquidweb-dev/undercurrent/RadioGroup').RadioGroupProps['onChange']} */
	const handleChange = (event, newValue) => {
		if (onChange) {
			onChange(event, newValue);
		}

		onValueChange(newValue);

		validateSimple(event.target.value, Boolean(required));
	};

	return (
		<FormControl
			required={required}
			error={Boolean(error)}
			{...formControlProps}
		>
			{label && (
				<FormLabel sx={{ mb: 100 }} {...formLabelProps}>
					{label}
				</FormLabel>
			)}
			<RadioGroup name={name} value={value} onChange={handleChange} {...rest} />
			{(helperText || error) && (
				<FormHelperText {...formHelperTextProps}>
					{helperText || error}
				</FormHelperText>
			)}
		</FormControl>
	);
};

export default HookedRadioGroup;
